import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import {
	Fx,
	Layout,
	Select,
	Table,
	Icon,
} from 'Components';
import {
	GetSystemRolesRequest,
	SystemRole,
	SystemRoleType
} from 'Services/Api/Roles/Types';
import { GET_ROLES_INITIAL_PARAMS, useRoles ,getValidPage} from './Roles.hooks';
import { useMemo, useEffect, useRef, useState } from 'react';
import { getNamesBySelectedRowKeys } from './Roles.helpers';
import useColumns from './columns';
import './styles/Roles.module.scss';
import { GROUPS_LABEL } from 'Pages/Groups/Groups.constants';
import { getAppRoutes } from 'Pages/App/App.constants';
import { generatePath, useHistory } from 'react-router-dom';
import { useTenants } from 'Components/Sidebar/hooks/useTenants';
import RolesActions from './RolesActions';
import { useStores } from 'Hooks/useStore';
import DebounceInput from 'Components/DebouncedInput';
import styles from '../../Pages/Reports/Reports.module.scss';
import EmptyState from 'PhishingManager/components/EmptyState';

import {
	deleteTenantCustomRole,
	resetPrimaryRoleOverridePermissions
} from './service/index.Service';
import RolesCardDashboard from './components/RolesCardDashboard';

import { Modal } from 'Components';
import { useIsMobile } from 'Hooks';
import RolesMobileView from './components/MobileView';
import PaginationElement from 'PhishingManager/components/Pagination';
import { useDashboardContext } from 'Pages/Dashboard/DashboardContext';
import { getErrorMessage } from './apiHelper';


function Roles() {
	const { t } = useTranslation();
	const {
		refetch,
		dataSource,
		onTableChange,
		selectedRowKeys,
		selectedTenantId,
		onTenantChange,
		result,
		loading,
		setLoading,
		page,
		pageSize,
		total,
		params,
		onPaginationChange,
		setRoleName
	} = useRoles();

	const { authStore, systemsStore } = useStores();
	const { loading: tenantLoading } = useTenants();
	const { tenantList, selectedTenant } = systemsStore;
	const isMobile = useIsMobile();
	const [mobileRecords, setMobileRecords] = useState<SystemRole[]>([]);
	const [restore, setRestore] = useState(false);
	const [deleteRecord, setDeleteRecord] = useState<
		SystemRole | null | undefined
	>(null);
	const [restoreRecord, setRestoreRecord] = useState<
		SystemRole | null | undefined
	>(null);
	const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);
	const [search, setSearch] = useState("");
	const { setToastInfo } = useDashboardContext();
	const isSuperAdmin =
		authStore.currentUser?.primarySystemRole?.role ===
		SystemRoleType.SuperAdmin;
	
	const PAGE_SIZE = 10;

	const selectedTenantValue = useMemo(() => {
		if (isSuperAdmin) {
			return selectedTenant?.value ?? 0;
		}
		return selectedTenant?.value || tenantList[0]?.value;
	}, [isSuperAdmin, selectedTenant, tenantList]);

	const tenantRef = useRef(selectedTenantValue);

	useEffect(() => {
		tenantRef.current = selectedTenantValue;
	}, [selectedTenantValue]);

	useEffect(() => {
		setSearch("");
	  }, [selectedTenantValue]);
	const TENANTS = useMemo(() => {
		if (!isSuperAdmin) {
			return tenantList;
		}

		return [
			{
				label: t(GROUPS_LABEL.ALL_TENANTS) as string,
				value: 0
			},
			...tenantList
		];
	}, [isSuperAdmin, tenantList, t]);

	const handleTenantChange = (id: number | string) => {
		const tenantId = Number(id);

		if (tenantId === 0 && isSuperAdmin) {
			systemsStore.changeTenant({
				label: t(GROUPS_LABEL.ALL_TENANTS) as string,
				value: 0
			});
			onTenantChange(0);
			return;
		}

		const tenant = tenantList.find((t) => t.value === tenantId);
		if (!tenant) return;
		systemsStore.changeTenant(tenant);
		onTenantChange(tenant.value);
	};

	const namesBySelectedRowKeys = useMemo(
		() => getNamesBySelectedRowKeys(dataSource, selectedRowKeys),
		[dataSource, selectedRowKeys]
	);

	const history = useHistory();
	const columns = useColumns<SystemRole>({
		params,
		actions: {
			onViewDetail: (record) =>
				history.push(
					generatePath(getAppRoutes().VIEW_ROLE, {
						roleId: record.roleName
					})
				),

			onEdit: (record) =>
				history.push(
					generatePath(record.type?.toLowerCase() === "primary" ? getAppRoutes().EDIT_ROLE: getAppRoutes().EDIT_SECONDARY_ROLE, {
						roleId: record.roleName
					})
				),

			onClone: (record) =>
				history.push(
					generatePath(getAppRoutes().CLONE_ROLE, {
						roleId: record.roleName
					})
				),

			onRestoreToDefaults: async (record) => {
				setRestoreRecord(record);
				setRestore(true);
			},

			onDelete: async (record) => {
				setDeleteRecord(record);
			},
			selectedTenant: selectedTenantValue
		},
	});

	const handleSearchChange = (value: string) => {
		const variables: GetSystemRolesRequest = {
			sort: result.variables?.sort ?? GET_ROLES_INITIAL_PARAMS.sort,
			filters: {
				...result.variables?.filters,
				roleName: value
			}
		};
		setRoleName(value);
		refetch(variables, tenantRef?.current, 1, pageSize);
	};
	useEffect(() => {
		handleSearchChange(search);
	  }, [search]);
	useEffect(() => {
		if (!!isSuperAdmin) {
			if (selectedTenantValue !== selectedTenantId) {
				onTenantChange(selectedTenantValue);
			}
			return;
		} else {
			if (!!selectedTenantValue) {
				onTenantChange(selectedTenantValue);
			}
		}
	}, [isSuperAdmin, selectedTenantValue]);

	useEffect(() => {
		if (!isSuperAdmin && tenantList.length && !selectedTenant?.value) {
			systemsStore.changeTenant(tenantList[0]);
		}
	}, [tenantList, isSuperAdmin, selectedTenant]);

	const handleRestoreOk = async () => {
		if (!restoreRecord?.id) return;
		try {
			setLoading(true);
			await resetPrimaryRoleOverridePermissions(
				restoreRecord.id
			);
			setToastInfo?.({
				message:  t('notification.role.restore'),
				type: 'success',
				icon: 'check',
			});
			
			refetch();
		} catch(error){
			const message = getErrorMessage(error, t("errorCodes.genericErrorMessage"));
			setToastInfo?.({
				message: message,
				type: 'warning',
				icon: 'campaignInfo',
			});
		} finally {
			setLoading(false);
			setRestore(false);
			setRestoreRecord(null);
		}
	};

	const handleRestoreCancel = () => {
		setRestore(false);
		setRestoreRecord(null);
	};

	const handleDeleteOk = async () => {
		if (!deleteRecord?.id) return;
		try {
			setLoading(true);
			await deleteTenantCustomRole(deleteRecord.id);setToastInfo?.({
				message: t('notification.delete.success', { name: 'Role' }),
				type: 'success',
				icon: 'check',
			});
			const nextPage = getValidPage(
				page,
				total - 1,
				pageSize
			);
			await refetch(params,selectedTenantId,nextPage,pageSize
			);
			setDashboardRefreshKey((prev) => prev + 1);
		} catch(error){
			const message = getErrorMessage(error, t("errorCodes.genericErrorMessage"));
			setToastInfo?.({
				message: message,
				type: 'warning',
				icon: 'campaignInfo',
			});
		} finally {
			setLoading(false);
			setDeleteRecord(null);
		}
	};

	const handleDeleteCancel = () => {
		setDeleteRecord(null);
	};
	
	useEffect(() => {
		if (!isMobile) return;
		if (page === 1) {
			setMobileRecords(dataSource);
		}
	}, [dataSource, page, isMobile]);

	return (
		<Layout className={`${styles.saved} layout-roles` }>
			
			<div className="role-header">
				<Fx
					align="middle"
					className="role-header-content"
					justify="space-between"
				>
					<div className='role-header-wrapper'>
						<p className="role-title">{t('roles.title')}</p>
						<p className="role-description">{t('roles.description')}</p>
					</div>
					{!!isSuperAdmin && (
						<div className="pm-dropdown-section">
							<Select
								id="pm-client-select"
								placeholder={t('phishingManager.selectTenant')}
								className='custom-select'
								value={selectedTenantValue}
								onChange={handleTenantChange}
								showSearch
								loading={tenantLoading}
								disabled={!isSuperAdmin}
								optionFilterProp="label"
								suffixIcon={<Icon type="roleDropdown" />}
								options={TENANTS}
								getPopupContainer={(trigger) =>
									trigger.parentElement
								}
							/>
						</div>
					)}
				</Fx>
			</div>
			<div>
				<RolesCardDashboard
					tenantId={
						isSuperAdmin && Number(selectedTenantValue) === 0
							? undefined
							: selectedTenantValue
					}
					refreshKey={dashboardRefreshKey}
				/>
			</div>
			<div className="role-action-wrapper">
				<div className="role-select-wrapper__filter-group role-select-wrapper__role-search">
					<div className={styles.inputFilter}>
						<DebounceInput placeholder={t("phishingManager.campaignSetup.step1.search")} setDebouncedSearch={setSearch} debounceSearch={search}  />
					</div>
				</div>

				<RolesActions
					selectedTenantValue={selectedTenantValue}
					ids={selectedRowKeys as string[]}
					idsNames={namesBySelectedRowKeys}
					onSubmit={() => {
						console.log('submit');
					}}
					hasData={Boolean(dataSource.length)}
					dataSource={dataSource}
				/>
			</div>
			<Spin spinning={loading} wrapperClassName="spinner-class" >

				{!dataSource.length ? (
					<EmptyState
						title={t('roles.noData.title')}
						description={[]}
						actionLabel={null}
					/>
				) : isMobile ? (
					<RolesMobileView
						records={mobileRecords}
						t={t}
						totalCount={total}
						selectedTenant={selectedTenantValue}
						currentPage={page}
						setCurrentPage={() => onPaginationChange(page + 1, pageSize, true)}
						actions={{
							onViewDetail: (record) =>
								history.push(generatePath(getAppRoutes().VIEW_ROLE, { roleId: record.roleName })),
							onEdit: (record) =>
								history.push(generatePath(record.type?.toLowerCase() === "primary" ? getAppRoutes().EDIT_ROLE: getAppRoutes().EDIT_SECONDARY_ROLE, { roleId: record.roleName })),
							onClone: (record) =>
								history.push(generatePath(getAppRoutes().CLONE_ROLE, { roleId: record.roleName })),
							onRestoreToDefaults: async (record) => {
								setRestoreRecord(record);
								setRestore(true);
							},
							onDelete: async (record) => setDeleteRecord(record),
							selectedTenant: selectedTenantValue
						}}
					/>
				) : (
					<div className='roles-table-wrapper'>
						<Table<SystemRole>
							dataSource={dataSource.filter(Boolean)}
							rowKey="roleName"
							columns={columns}
							pagination={false}
							locale={{ emptyText: !selectedTenantValue ? 'No Tenant Selected' : '' }}
							onChange={onTableChange}
							totalCount={total}
							totalVisible
						/>
						<PaginationElement
							total={total}
							pageSize={PAGE_SIZE}
							current={page}
							onChange={onPaginationChange}
						/>
					</div>
				)}
			</Spin>

			{restore && (
				<Modal
					visible={restore}
					onCancel={handleRestoreCancel}
					centered
					title={t('roles.rolesActionsModals.restoreModal.title')}
					okText={t('roles.rolesActionsModals.restoreModal.okText')}
					cancelText={t('roles.rolesActionsModals.restoreModal.cancelText')}
					bodyStyle={{ padding: '12px 24px' }}
					onOk={handleRestoreOk}
					className="custom-role-modal"
				>

				<p  className ='card-description'>{t('roles.rolesActionsModals.restoreModal.description')}</p>
				</Modal>
			)}
			{deleteRecord && (
				<Modal
				visible={!!deleteRecord}
				centered
				title={t('roles.rolesActionsModals.deleteCustomRoleModal.title')}
				onCancel={handleDeleteCancel}
				okText={t('roles.rolesActionsModals.deleteCustomRoleModal.okText')}
				cancelText={t('roles.rolesActionsModals.deleteCustomRoleModal.cancelText')}
				bodyStyle={{ padding: '12px 24px' }}
				onOk={handleDeleteOk}
				className="custom-role-modal"
			  >
				<p className ='card-description'>{t('roles.rolesActionsModals.deleteCustomRoleModal.description')}</p>
			  </Modal>
			)}
		</Layout>
	);
}

export default Roles;
